import { type ClassValue, clsx } from 'clsx'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(isoWeek)
import { twMerge } from 'tailwind-merge'

import { RequestFilters } from '@/schema'

import { SortOrder } from '../types/common'

import {
  DATE_FORMAT,
  DEFAULT_SEARCH_REQUEST_BODY,
  DEFAULT_TABLE_SORT_BY,
  EArchiveExtension,
  EDocumentExtension,
  EMAIL_REGEX,
} from './constants'
import { ROUTES } from './routes'

/**
 * Splits an array into chunks of a specified size.
 *
 * @param array - The array to split
 * @param size - The size of each chunk (default: uses UPLOAD_FILE_BATCH_SIZE from constants)
 * @returns An array of chunks
 *
 * @example
 * ```tsx
 * const files = [1, 2, 3, 4, 5]
 * chunkArray(files, 2) // Returns: [[1, 2], [3, 4], [5]]
 * ```
 */
export function chunkArray<T>(array: T[], size: number = 20): T[][] {
  const chunks: T[][] = []
  const chunkSize = size
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

/**
 * Utility function to merge Tailwind CSS classes intelligently.
 *
 * This function combines the power of:
 * - clsx: For conditional class name building
 * - twMerge: For intelligently merging Tailwind classes (handles conflicts)
 *
 * @param inputs - Any number of class values (strings, objects, arrays)
 * @returns Merged class string with conflicts resolved
 *
 * @example
 * ```tsx
 * // Simple usage
 * cn('px-2 py-1', 'px-4') // Returns: 'py-1 px-4' (px-4 overrides px-2)
 *
 * // With conditional classes
 * cn('text-base', isActive && 'text-blue-500', error && 'text-red-500')
 *
 * // With objects
 * cn('btn', { 'btn-primary': isPrimary, 'btn-disabled': isDisabled })
 *
 * // In components with prop overrides
 * <div className={cn('default-class', className)} />
 * ```
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Merges partial table pagination parameters with default values.
 *
 * This utility function ensures that any provided pagination parameters are merged
 * with the default values, filling in any missing fields. This is useful when you
 * only want to override specific parameters while keeping the rest as defaults.
 *
 * **Key Features:**
 * - Accepts partial parameters (any subset of TablePaginationParams)
 * - Merges with DEFAULT_TABLE_PAGINATION_PARAMS to ensure all fields are present
 * - Handles filter_by object merging (shallow merge - provided filters override defaults)
 * - Type-safe: returns a complete TablePaginationParams object
 *
 * **Input Parameters:**
 * @param data - Partial table pagination parameters to merge with defaults.
 *               Can contain any subset of: filter_by, limit, offset, q, sort_by
 *               If a key is provided, it will override the default value.
 *               If a key is not provided, the default value will be used.
 *
 * **Expected Output:**
 * @returns Complete TablePaginationParams object with all fields populated.
 *          Provided values override defaults, missing values use defaults.
 *
 * **Main Logic:**
 * 1. Start with DEFAULT_TABLE_PAGINATION_PARAMS as the base
 * 2. If data.filter_by is provided, merge it with default filter_by (shallow merge)
 * 3. Override any other provided fields (limit, offset, q, sort_by)
 * 4. Return the complete merged object
 *
 * @example
 * ```typescript
 * // Merge with only limit override
 * const params1 = mergeTablePaginationParams({ limit: 50 })
 * // Result: { filter_by: {}, limit: 50, offset: 0, q: '', sort_by: '+created_at' }
 *
 * // Merge with multiple overrides
 * const params2 = mergeTablePaginationParams({
 *   limit: 100,
 *   sort_by: '-name',
 *   q: 'search term'
 * })
 * // Result: { filter_by: {}, limit: 100, offset: 0, q: 'search term', sort_by: '-name' }
 *
 * // Merge with filter_by (shallow merge)
 * const params3 = mergeTablePaginationParams({
 *   filter_by: { status: 'active' },
 *   offset: 20
 * })
 * // Result: { filter_by: { status: 'active' }, limit: 20, offset: 20, q: '', sort_by: '+created_at' }
 *
 * // Empty object uses all defaults
 * const params4 = mergeTablePaginationParams({})
 * // Result: Same as DEFAULT_TABLE_PAGINATION_PARAMS
 * ```
 */
export function mergeTablePaginationParams<T extends RequestFilters>(
  data: Partial<T> = {}
): T {
  // Start with default values
  const merged: T = {
    ...(DEFAULT_SEARCH_REQUEST_BODY as T),
  }

  // Handle filter_by separately - merge objects if both exist
  if (data.filter_by !== undefined) {
    merged.filter_by = {
      ...DEFAULT_SEARCH_REQUEST_BODY.filter_by,
      ...data.filter_by,
    }
  }

  // Override other fields if provided
  if (data.limit !== undefined) {
    merged.limit = data.limit
  }

  if (data.offset !== undefined) {
    merged.offset = data.offset
  }

  if (data.q !== undefined) {
    merged.q = data.q
  }

  if (data.sort_by !== undefined) {
    merged.sort_by = data.sort_by
  }

  if (data.filter_only !== undefined) {
    merged.filter_only = data.filter_only
  }

  return merged
}

/**
 * Converts store sort state to API sort_by format.
 *
 * This utility function converts the internal store sort representation (with separate
 * column and direction properties) to the API's sort_by format, which uses a single
 * string with a prefix: "+column" for ascending, "-column" for descending.
 *
 * **Input Parameters:**
 * @param sort - Sort state object containing:
 *               - column: Column name to sort by (string) or null
 *               - direction: 'asc' for ascending, 'desc' for descending, or null
 * @param defaultSortBy - Optional default sort_by value to use when column or direction is null.
 *                        Defaults to DEFAULT_TABLE_SORT_BY ('+created_at')
 *
 * **Expected Output:**
 * @returns API format sort_by string:
 *          - "+column" when direction is 'asc'
 *          - "-column" when direction is 'desc'
 *          - defaultSortBy when column or direction is null
 *
 * **Main Logic:**
 * 1. Check if column and direction are both non-null
 * 2. If valid, return "+column" for ascending or "-column" for descending
 * 3. If either is null, return the default sort_by value
 *
 * **Use Cases:**
 * - Convert store sort state to API request format
 * - Prepare sort parameters for API calls
 * - Handle null/undefined sort states gracefully
 *
 * @example
 * ```typescript
 * // Convert ascending sort
 * const sort1 = { column: 'name', direction: 'asc' as const }
 * const apiSort1 = convertSortToApiFormat(sort1)
 * // Result: '+name'
 *
 * // Convert descending sort
 * const sort2 = { column: 'created_at', direction: 'desc' as const }
 * const apiSort2 = convertSortToApiFormat(sort2)
 * // Result: '-created_at'
 *
 * // Handle null values (uses default)
 * const sort3 = { column: null, direction: 'asc' as const }
 * const apiSort3 = convertSortToApiFormat(sort3)
 * // Result: '+created_at' (default)
 *
 * // Use with store
 * const { sort } = useCompanyStore()
 * const sortBy = convertSortToApiFormat(sort)
 * // Use sortBy in API request
 * ```
 */
export function convertSortToApiFormat(
  sort: {
    column: string | null
    direction: SortOrder
  },
  defaultSortBy: string = DEFAULT_TABLE_SORT_BY
): string {
  // If column or direction is null, return default
  if (!sort.column || !sort.direction) {
    return defaultSortBy
  }

  // Convert to API format: "+column" for ascending, "-column" for descending
  const prefix = sort.direction === 'asc' ? '+' : '-'
  return `${prefix}${sort.column}`
}

/**
 * Formats a date string or Date object into a localized date string.
 *
 * This utility function provides consistent date formatting across the application,
 * using the 'en-US' locale with format: "MMM DD, YYYY" (e.g., "Jan 15, 2024").
 *
 * **Key Features:**
 * - Accepts string (ISO date) or Date object
 * - Returns formatted date in "MMM DD, YYYY" format
 * - Returns '-' for null/undefined/invalid dates
 * - Type-safe and consistent formatting
 *
 * @param value - Date value to format (string, Date, or null/undefined)
 * @returns Formatted date string or '-' if invalid
 *
 * @example
 * ```tsx
 * // With ISO string
 * formatDisplayDate('2024-01-15T10:30:00Z') // Returns: 'Jan 15, 2024'
 *
 * // With Date object
 * formatDisplayDate(new Date()) // Returns: 'Nov 21, 2025'
 *
 * // With null/undefined
 * formatDisplayDate(null) // Returns: '-'
 * formatDisplayDate(undefined) // Returns: '-'
 *
 * // In table columns
 * renderCell: (value) => <span>{formatDisplayDate(value)}</span>
 *
 * // In components
 * <span>{formatDisplayDate(project.created_at)}</span>
 * ```
 */
export function formatDisplayDate(
  value: string | Date | null | undefined,
  format: string = DATE_FORMAT
): string {
  if (!value) return '-'

  try {
    const date = dayjs(value)
    if (!date.isValid()) return '-'

    return date.format(format)
  } catch {
    return '-'
  }
}

export function formatFullName(firstName: string, lastName: string): string {
  return `${firstName || ''} ${lastName || ''}`.trim()
}

/**
 * Gets the start of the day in UTC, preserving the selected date's local representation.
 */
export const getStartOfDayUTC = (date: dayjs.ConfigType): string => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD') + 'T00:00:00.000Z'
}

/**
 * Gets the end of the day in UTC, preserving the selected date's local representation.
 */
export const getEndOfDayUTC = (date: dayjs.ConfigType): string => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD') + 'T23:59:59.999Z'
}

/**
 * Formats a number with comma separators for thousands.
 *
 * @param value - The number to format
 * @returns Formatted number string with commas (e.g., "1,234")
 *
 * @example
 * ```tsx
 * formatNumber(1234) // Returns: "1,234"
 * formatNumber(1000) // Returns: "1,000"
 * formatNumber(999) // Returns: "999"
 * formatNumber(1234567) // Returns: "1,234,567"
 * ```
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('en-US')
}

/**
 * Validate if a file is an archive file
 */
export function isArchiveFile(fileName: string): boolean {
  return (
    fileName.toLowerCase().endsWith(EArchiveExtension.ZIP) ||
    fileName.toLowerCase().endsWith(EArchiveExtension.SEVEN_ZIP)
  )
}

export const timeout = (ms = 1000): Promise<void> => {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

export const wait = timeout

/**
 * Formats a number of days into a human-readable string with months, weeks, and days.
 *
 * This utility function converts numeric days into a localized, readable format.
 * It uses i18n for localization support across different languages.
 *
 * @param days - The number of days to format
 * @param t - Translation function from useTranslation hook (optional for non-i18n usage)
 * @returns Formatted time string (e.g., "1 month 2 weeks 3 days")
 *
 * @example
 * ```tsx
 * // Using with i18n
 * const { t } = useTranslation()
 * formatDays(45, t) // Returns: "1 month 2 weeks"
 *
 * // Using without i18n (defaults to English)
 * formatDays(45) // Returns: "1 month 2 weeks"
 * ```
 */
export const formatDays = (
  days: number,
  t?: (key: string) => string
): string => {
  // Fallback to English strings if no translation function provided
  const translate =
    t ||
    ((key: string) => {
      const fallback: Record<string, string> = {
        'common.time.zeroDays': '0 days',
        'common.time.month': 'month',
        'common.time.months': 'months',
        'common.time.week': 'week',
        'common.time.weeks': 'weeks',
        'common.time.day': 'day',
        'common.time.days': 'days',
      }
      return fallback[key] || key
    })

  if (days <= 0) return translate('common.time.zeroDays')

  const DAYS_IN_MONTH = 30
  const DAYS_IN_WEEK = 7

  const months = Math.floor(days / DAYS_IN_MONTH)
  let remaining = days % DAYS_IN_MONTH

  const weeks = Math.floor(remaining / DAYS_IN_WEEK)
  remaining = remaining % DAYS_IN_WEEK

  const parts: string[] = []

  if (months > 0) {
    const monthKey = months === 1 ? 'common.time.month' : 'common.time.months'
    parts.push(`${months} ${translate(monthKey)}`)
  }

  if (weeks > 0) {
    const weekKey = weeks === 1 ? 'common.time.week' : 'common.time.weeks'
    parts.push(`${weeks} ${translate(weekKey)}`)
  }

  if (remaining > 0) {
    const dayKey = remaining === 1 ? 'common.time.day' : 'common.time.days'
    parts.push(`${remaining} ${translate(dayKey)}`)
  }

  return parts.join(' ')
}

/**
 * Validate if a file is an HTML file
 */
export const isHTMLDocument = (
  fileName: string | null | undefined
): boolean => {
  if (!fileName) return false
  return (
    fileName.toLowerCase().endsWith(EDocumentExtension.HTML) ||
    fileName.toLowerCase().endsWith(EDocumentExtension.HTM)
  )
}

/**
 * Validate if a file is a PDF file
 */
export const isPDFDocument = (fileName: string | null | undefined): boolean => {
  if (!fileName) return false
  return fileName.toLowerCase().endsWith(EDocumentExtension.PDF)
}

/**
 * Calculates the time elapsed from a given timestamp to now
 * @param timestamp - The timestamp to calculate from (string or Date)
 * @returns A formatted string representing the time elapsed
 */
export const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date()
  const past = new Date(timestamp)
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)

  // 0-59 seconds: "Just now"
  if (diffInSeconds < 60) {
    return 'Just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)

  // 1-59 minutes: "{n} min"
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)

  // 1-23 hours: "{n} hr"
  if (diffInHours < 24) {
    return `${diffInHours} hr`
  }

  const diffInDays = Math.floor(diffInHours / 24)

  // Exactly 1 day: "Yesterday"
  if (diffInDays === 1) {
    return 'Yesterday'
  }

  // 2-6 days: "{n} day"
  if (diffInDays < 7) {
    return `${diffInDays} day`
  }

  const diffInWeeks = Math.floor(diffInDays / 7)

  // 1-4 weeks: "{n} wk"
  if (diffInWeeks < 4) {
    return `${diffInWeeks} wk`
  }

  const diffInMonths = Math.floor(diffInDays / 30)

  // 1-11 months: "{n} mo"
  if (diffInMonths < 12) {
    return `${diffInMonths} mo`
  }

  const diffInYears = Math.floor(diffInDays / 365)

  // 1+ years: "{n} yr"
  return `${diffInYears} yr`
}

export const createKeyboardClickHandler =
  (onClick: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

/**
 * Validate email address format and length
 * Inline validation - executes immediately when email input is not empty
 *
 * @param email - Email address to validate
 * @returns Error message if invalid, empty string if valid
 *
 * @example
 * validateEmail('user@example.com') // ''
 * validateEmail('invalid-email') // 'Please enter a valid email address'
 */
export const validateEmail = (email: string): string => {
  // Don't validate empty email (let required validation handle it)
  if (!email || email.trim() === '') return ''

  // Check RFC format
  if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address'
  return ''
}

/**
 * Get the redirect URL for the invite users page
 * @returns The redirect URL for the invite users page
 */
export const inviteUsersRedirectUrl = (): string => {
  return `${globalThis.location.origin}${ROUTES.INVITE}`
}
